import { limitLabel } from '@flexbyt/shared';
import { SourceGlyph } from '@flexbyt/widget-preview';
import { createFileRoute } from '@tanstack/react-router';
import { PageHero } from '../components/ui/hero.js';
import { AgenciesScene } from '../components/mascot/scenes.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { WidgetShowcase } from '../components/WidgetShowcase.js';
import {
    AGENCY_RELEVANT_PLANS,
    EXAMPLE_CLIENTS,
    NOT_SUPPORTED,
    SUPPORTED_CAPABILITIES,
    offerFor,
    planLimits,
    syncsAutomatically,
} from '../content/agency.js';
import { DIRECT_INTEGRATIONS, integrationPath } from '../content/integrations.js';
import { PLATFORM_CONTENT } from '../content/platforms.js';
import { sampleSetForSource } from '../content/sample-sets.js';
import {
    WIDGET_BUILDER_PATH,
    WIDGET_CONTENT,
    WIDGET_COUNT_WORD,
    WIDGET_COUNT_WORD_CAPITALISED,
    widgetPath,
} from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';

/**
 * The agency page.
 *
 * ===========================================================================
 * Its argument is a small one, made honestly
 * ===========================================================================
 * ReviewsJet is not an agency platform and this page does not pretend to be
 * selling one. There is no white labelling, no client portal, no team seats, no
 * templates, no consolidated invoice and no dashboard across clients — every one
 * of those was traced through the API and came back false.
 *
 * What is true is smaller and still worth an agency's attention: the reviews
 * section on a client site stops being a bespoke build. That is the whole
 * pitch, and a page that made a bigger one would be found out on day one — by
 * exactly the audience least forgiving of it, since agencies evaluate tools for
 * a living.
 *
 * So there is a section on this page listing what ReviewsJet does *not* do. It
 * is the most useful thing here: an agency deciding whether to standardise on
 * something needs the limits before they commit three clients to it, and being
 * told plainly is worth more than being sold to.
 */
export const Route = createFileRoute('/agencies')({
  head: () => {
    const { meta, links } = seo({
      title: 'ReviewsJet for agencies and freelancers',
      description:
        'Stop building a bespoke reviews section for every client. Import their Google, Trustpilot, Yelp, Tripadvisor, Airbnb or app store reviews, publish the good ones, paste one line — on any site you build.',
      path: '/agencies',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'For agencies', path: '/agencies' },
            ])
          ),
        },
      ],
    };
  },
  component: Agencies,
});

function Agencies() {
  return (
    <>
      {/* ------------------------------------------------------------ Hero */}
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'For agencies', path: '/agencies' },
        ]}
        art={<AgenciesScene />}
      >
        <Measure>
          <Eyebrow>For agencies and freelancers</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            Stop building a reviews section <Mark>from scratch</Mark> on every
            client site.
          </Heading>
          <Text size="lead" className="mt-6">
            Every client has reviews somewhere — Google, Trustpilot, Yelp,
            Tripadvisor, Airbnb, the app stores — and every client wants them on the site you
            are building. ReviewsJet turns that from an afternoon of bespoke work
            into ten minutes and one line of code.
          </Text>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl('agencies-hero')}
              size="lg"
              onClick={() =>
                track('agency_cta_clicked', { surface: 'agencies-hero' })
              }
            >
              Start free on your next build
            </ButtonAnchor>
            <ButtonLink to={WIDGET_BUILDER_PATH} variant="secondary" size="lg">
              Try the widget builder
            </ButtonLink>
          </div>

          <p className="mt-5 text-caption text-ink-500">
            No card, no sales call. Every layout works on the free plan, so you
            can build the section before the client has paid for anything.
          </p>
        </Measure>
      </PageHero>

      {/* ------------------------------------------------- The client roster */}
      <Section aria-labelledby="roster-heading">
        <Measure>
          <Heading level={2} id="roster-heading">
            Three clients, three platforms, one workflow.
          </Heading>
          <Text className="mt-4 max-w-prose">
            Each client site is its own ReviewsJet workspace with its own review
            sources and its own widget. What does not change from client to
            client is what you have to do.
          </Text>
        </Measure>

        <ul className="mt-10 grid gap-4 lg:grid-cols-3">
          {EXAMPLE_CLIENTS.map((client) => (
            <li
              key={client.name}
              className="flex flex-col rounded-xl bg-white p-5 ring-1 ring-ink-150"
            >
              <p className="text-title text-ink-950">{client.name}</p>
              <p className="mt-1 text-caption text-ink-500">{client.kind}</p>

              <dl className="mt-5 space-y-3 border-t border-ink-100 pt-4 text-body-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-ink-500">Built on</dt>
                  <dd className="text-ink-900">
                    {PLATFORM_CONTENT[client.platform].name}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-ink-500">Reviews from</dt>
                  <dd className="flex items-center gap-1.5">
                    {client.sources.map((source) => (
                      <SourceGlyph
                        key={source}
                        source={source}
                        className="size-4"
                      />
                    ))}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="shrink-0 text-ink-500">Shows</dt>
                  <dd className="text-right text-ink-900">{client.widget}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>

        {/*
          The account-model note.

          The single most important sentence on this page, because it is the one
          an agency will otherwise assume wrongly. Stated here, next to the
          roster, rather than buried in a limits section further down.
        */}
        <div className="mt-8 max-w-text rounded-lg bg-ink-25 px-4 py-3.5 ring-1 ring-ink-150">
          <p className="text-body-sm text-ink-600">
            <span className="font-medium text-ink-950">
              One workspace per client site.
            </span>{' '}
            Installing the ReviewsJet app on a client&rsquo;s Wix or Webflow site
            adds that site under your login, and opening ReviewsJet from that
            site&rsquo;s dashboard takes you straight into it. On WordPress,
            Squarespace and anywhere else, each site is its own ReviewsJet
            account. There is no switcher and no combined dashboard.
          </p>
        </div>
      </Section>

      {/* --------------------------------------------------- The four steps */}
      <Section tone="raised" aria-labelledby="workflow-heading">
        <Measure>
          <Heading level={2} id="workflow-heading">
            What it replaces.
          </Heading>
          <Text className="mt-4 max-w-prose">
            The bespoke version of this is an API you have to get keys for, a
            component you have to build, a cache you have to invent and a client
            who emails you when a review is out of date. This is the other
            version.
          </Text>
        </Measure>

        <ol className="mt-10 grid gap-8 md:grid-cols-4">
          {[
            {
              n: '01',
              title: 'Paste their listing',
              body: 'Their Google, Trustpilot, Yelp, Tripadvisor or Airbnb page, or their app’s store page. The reviews come across.',
            },
            {
              n: '02',
              title: 'Publish the good ones',
              body: 'Nothing appears on the site until you say so. Bulk-publish what works, leave the rest.',
            },
            {
              n: '03',
              title: 'Style it to the design',
              body: `Colours, border, radius, what each card shows. ${WIDGET_COUNT_WORD_CAPITALISED} layouts, all on the free plan.`,
            },
            {
              n: '04',
              title: 'Paste one line',
              body: 'Or install the app on Wix and Webflow. New reviews appear without touching the site again.',
            },
          ].map((step) => (
            <li key={step.n}>
              <p className="flex items-baseline gap-3">
                <span className="nums text-body-sm font-semibold text-ink-300">
                  {step.n}
                </span>
                <span className="text-title text-ink-950">{step.title}</span>
              </p>
              <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </Section>

      {/* ---------------------------------------------------- The end result */}
      <Section aria-labelledby="result-heading">
        <Measure>
          <Heading level={2} id="result-heading">
            What lands on the client&rsquo;s site.
          </Heading>
          <Text className="mt-4 max-w-prose">
            The real widget, styled to the design you are already building. Not
            a third-party badge dropped into a corner.
          </Text>
        </Measure>

        <div className="rj-canvas mt-10 rounded-xl p-4 ring-1 ring-ink-150 sm:p-8">
          <WidgetShowcase
            type="masonry"
            reviews={sampleSetForSource('tripadvisor').reviews.slice(0, 6)}
          />
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <ButtonLink to={WIDGET_BUILDER_PATH}>Build one now, free</ButtonLink>
          <ButtonLink to="/widgets" variant="secondary">
            See all {WIDGET_COUNT_WORD} layouts
          </ButtonLink>
        </div>
      </Section>

      {/* ------------------------------------------------ What it costs them */}
      <Section tone="raised" aria-labelledby="pricing-heading">
        <Measure>
          <Heading level={2} id="pricing-heading">
            What a client site costs.
          </Heading>
          <Text className="mt-4 max-w-prose">
            Per site, not per agency — each client&rsquo;s workspace has its own
            plan. Most agencies put the free plan on a build and let the client
            decide whether to upgrade once they can see it working.
          </Text>
        </Measure>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[34rem] border-collapse text-left">
            <thead>
              <tr className="border-b border-ink-200">
                <th scope="col" className="py-3 pr-4 text-caption font-semibold text-ink-950">
                  Plan
                </th>
                <th scope="col" className="py-3 pr-4 text-caption font-semibold text-ink-950">
                  Per month
                </th>
                <th scope="col" className="py-3 pr-4 text-caption font-semibold text-ink-950">
                  Published reviews
                </th>
                <th scope="col" className="py-3 pr-4 text-caption font-semibold text-ink-950">
                  Review sources
                </th>
                <th scope="col" className="py-3 text-caption font-semibold text-ink-950">
                  Syncs daily
                </th>
              </tr>
            </thead>
            <tbody>
              {AGENCY_RELEVANT_PLANS.map((id) => {
                const limits = planLimits(id);
                const offer = offerFor(id);
                return (
                  <tr key={id} className="border-b border-ink-100">
                    <th scope="row" className="py-3.5 pr-4 text-body-sm font-medium text-ink-950">
                      {offer?.name ?? id}
                    </th>
                    <td className="nums py-3.5 pr-4 text-body-sm text-ink-700">
                      {offer && offer.monthly > 0
                        ? `$${offer.monthly.toFixed(2)}`
                        : '$0'}
                    </td>
                    <td className="nums py-3.5 pr-4 text-body-sm text-ink-700">
                      {limitLabel(limits.reviews)}
                    </td>
                    <td className="nums py-3.5 pr-4 text-body-sm text-ink-700">
                      {limitLabel(limits.integrations)}
                    </td>
                    <td className="py-3.5 text-body-sm text-ink-700">
                      {syncsAutomatically(id) ? (
                        'Yes'
                      ) : (
                        <span className="text-ink-500">Manual refresh</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-4 max-w-prose text-caption text-ink-500">
          All {WIDGET_COUNT_WORD} layouts work on every plan, including free. Any paid plan
          removes the small &ldquo;Powered by ReviewsJet&rdquo; line from the
          widget.{' '}
          <SiteLink
            to="/pricing"
            className="font-medium text-accent-600 underline decoration-accent-200 underline-offset-[3px] hover:text-accent-700"
          >
            Full pricing
          </SiteLink>
          .
        </p>
      </Section>

      {/* ------------------------------------------------ Honest about limits */}
      <Section aria-labelledby="limits-heading">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-16">
          <div>
            <Heading level={2} id="limits-heading">
              What ReviewsJet is not.
            </Heading>
            <Text className="mt-4 max-w-prose">
              You evaluate tools for a living, so here is the part most pages
              leave out. ReviewsJet is a per-site product with an agency-shaped
              use case, not an agency platform — and if any of the following is
              a requirement, it is the wrong tool.
            </Text>
            <Text className="mt-4 max-w-prose">
              None of this is on a roadmap we are prepared to promise. If it
              changes, this page changes.
            </Text>
          </div>

          <div>
            <ul className="space-y-5">
              {NOT_SUPPORTED.map((entry) => (
                <li key={entry.id} className="flex gap-3.5">
                  <svg
                    viewBox="0 0 20 20"
                    aria-hidden
                    className="mt-1 size-4 shrink-0 text-ink-300"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  >
                    <path d="M5 5l10 10M15 5L5 15" />
                  </svg>
                  <div>
                    <p className="text-body-sm font-medium text-ink-950">
                      {entry.label}
                    </p>
                    <p className="mt-1 max-w-prose text-body-sm text-ink-600">
                      {entry.detail}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* -------------------------------------------------- What it does do */}
      <Section tone="raised" aria-labelledby="does-heading">
        <Measure>
          <Heading level={2} id="does-heading">
            What it does do.
          </Heading>
        </Measure>

        <ul className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {SUPPORTED_CAPABILITIES.map((entry) => (
            <li key={entry.id}>
              <p className="text-title text-ink-950">{entry.label}</p>
              <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                {entry.detail}
              </p>
            </li>
          ))}
        </ul>
      </Section>

      {/* --------------------------------------------------- Where to start */}
      <Section aria-labelledby="links-heading">
        <Heading level={2} id="links-heading" size="title">
          Where to start
        </Heading>

        <div className="mt-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <LinkGroup title="Your client’s reviews">
            {DIRECT_INTEGRATIONS.map((entry) => (
              <SiteLink
                key={entry.slug}
                to={integrationPath(entry)}
                className={LINK}
              >
                {entry.name}
              </SiteLink>
            ))}
          </LinkGroup>

          <LinkGroup title="The site you are building">
            {(['wix', 'webflow', 'wordpress', 'squarespace'] as const).map(
              (platform) => (
                <SiteLink
                  key={platform}
                  to={`/for/${PLATFORM_CONTENT[platform].slug}`}
                  className={LINK}
                >
                  {PLATFORM_CONTENT[platform].name}
                </SiteLink>
              )
            )}
          </LinkGroup>

          <LinkGroup title="Layouts">
            {WIDGET_CONTENT.slice(0, 4).map((widget) => (
              <SiteLink key={widget.slug} to={widgetPath(widget)} className={LINK}>
                {widget.seoName}
              </SiteLink>
            ))}
            <SiteLink to="/widgets" className={LINK}>
              All {WIDGET_COUNT_WORD_CAPITALISED}
            </SiteLink>
          </LinkGroup>

          <LinkGroup title="Try it without an account">
            <SiteLink to={WIDGET_BUILDER_PATH} className={LINK}>
              Widget builder
            </SiteLink>
            <SiteLink to="/tools" className={LINK}>
              All free tools
            </SiteLink>
            <SiteLink to="/pricing" className={LINK}>
              Pricing
            </SiteLink>
          </LinkGroup>
        </div>
      </Section>

      {/* ------------------------------------------------------------- CTA */}
      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Try it on the next site you build.
          </Heading>
          <Text className="mt-4 text-white/70">
            Free plan, no card, no call. If it saves you an afternoon on one
            client site, it will save you an afternoon on all of them.
          </Text>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl('agencies-close')}
              variant="inverse"
              size="lg"
              onClick={() =>
                track('agency_cta_clicked', { surface: 'agencies-close' })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink
              to={WIDGET_BUILDER_PATH}
              variant="inverse-ghost"
              size="lg"
            >
              Build a widget first
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}

const LINK =
  'block text-body-sm text-ink-600 underline-offset-[3px] hover:text-ink-950 hover:underline';

function LinkGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-caption font-semibold text-ink-950">{title}</p>
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}
